import { useUpdateBooking } from '@hooks/accommodation';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { AccommodationBooking } from '../../../../types/accommodation';
import { apiError } from '../../../../util/notify';

interface ExtendStayModalProps {
  isOpen: boolean;
  booking: AccommodationBooking | null;
  onClose: () => void;
}

const addDays = (isoDate: string, days: number): string => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const formatDateDisplay = (isoDate: string): string =>
  new Date(isoDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export const ExtendStayModal: React.FC<ExtendStayModalProps> = ({
  isOpen,
  booking,
  onClose,
}) => {
  const updateBooking = useUpdateBooking();
  const isSubmitting = updateBooking.isPending;

  const [extraNights, setExtraNights] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setExtraNights(1);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  if (!isOpen || !booking) return null;

  const checkInMs = new Date(booking.check_in).getTime();
  const checkOutMs = new Date(booking.check_out).getTime();
  const originalNights = Math.max(
    1,
    Math.round((checkOutMs - checkInMs) / 86_400_000),
  );
  const dailyRate = booking.total_amount / originalNights;
  const safeExtraNights = Math.max(1, Math.floor(extraNights));
  const extensionCost = dailyRate * safeExtraNights;
  const newTotalAmount = booking.total_amount + extensionCost;
  const newCheckOut = addDays(booking.check_out, safeExtraNights);

  const fmt = (n: number) =>
    n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (safeExtraNights < 1) {
      apiError('Enter at least 1 night to extend.');
      return;
    }
    updateBooking.mutate(
      {
        id: booking.id,
        updates: {
          check_out: newCheckOut,
          total_amount: Math.round(newTotalAmount),
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Extend stay</h3>
            <p className="text-xs text-gray-400">
              {booking.client
                ? `${booking.client.first_name} ${booking.client.last_name}`
                : `Booking #${booking.id.slice(0, 8).toUpperCase()}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={15} />
          </button>
        </div>

        {/* Current booking summary */}
        <div className="mx-5 mt-4 space-y-1">
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
            <span className="text-xs text-gray-500">Current check-out</span>
            <span className="text-xs font-semibold text-gray-800">
              {formatDateDisplay(booking.check_out)}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
            <span className="text-xs text-gray-500">Original stay</span>
            <span className="text-xs font-semibold text-gray-800">
              {originalNights} night{originalNights !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
            <span className="text-xs text-gray-500">Daily rate</span>
            <span className="text-xs font-semibold text-gray-800">
              {booking.currency} {fmt(dailyRate)} / night
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {/* Extra nights */}
          <div>
            <label
              htmlFor="es-nights"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Extend by (nights) *
            </label>
            <input
              id="es-nights"
              ref={inputRef}
              type="number"
              min={1}
              step={1}
              value={extraNights}
              onChange={(e) =>
                setExtraNights(Math.max(1, Math.floor(Number(e.target.value))))
              }
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              required
            />
          </div>

          {/* Extension preview */}
          <div className="space-y-1">
            <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
              <span className="text-xs text-gray-500">Extension cost</span>
              <span className="text-xs font-semibold text-gray-800">
                {booking.currency} {fmt(extensionCost)}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
              <span className="text-xs text-gray-500">New check-out date</span>
              <span className="text-xs font-semibold text-gray-800">
                {formatDateDisplay(newCheckOut)}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-pos-primary/10 px-4 py-3">
              <span className="text-xs font-semibold text-pos-primary">
                New total
              </span>
              <span className="text-sm font-bold text-pos-primary">
                {booking.currency} {fmt(newTotalAmount)}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Extending…' : 'Extend stay'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
